// src/components/ShipmentTimeline.js

import { useState, useMemo } from 'react';
import {
  Calendar,
  Clock,
  Package,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Truck,
  ZoomIn,
  ZoomOut,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

// Status colors
const STATUS_CONFIG = {
  'Delivered': { color: 'success', icon: CheckCircle },
  'In Transit': { color: 'info', icon: Truck },
  'Pending': { color: 'warning', icon: Clock },
  'Delayed': { color: 'danger', icon: AlertTriangle },
};

// Format date for display
function formatDate(date) {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

// Timeline Bar Component
function TimelineBar({ shipment, startDate, endDate, totalDays, onSelect, colors }) {
  const shipDate = new Date(shipment.shipDate);
  const deliveryDate = shipment.status === 'Delivered' 
    ? new Date(shipDate.getTime() + shipment.transitHours * 60 * 60 * 1000)
    : new Date();

  const startOffset = Math.max(0, (shipDate - startDate) / (1000 * 60 * 60 * 24));
  const duration = (deliveryDate - shipDate) / (1000 * 60 * 60 * 24);
  
  const leftPercent = (startOffset / totalDays) * 100;
  const widthPercent = Math.max(2, (duration / totalDays) * 100);

  const statusConfig = STATUS_CONFIG[shipment.status] || STATUS_CONFIG['Pending'];
  const barColor = colors[statusConfig.color];
  const hasExcursion = shipment.hasExcursion;

  return (
    <div
      style={{
        position: 'absolute',
        left: `${leftPercent}%`,
        width: `${Math.min(widthPercent, 100 - leftPercent)}%`,
        height: '32px',
        top: '50%',
        transform: 'translateY(-50%)',
        cursor: 'pointer',
      }}
      onClick={() => onSelect(shipment)}
    >
      <div
        style={{
          height: '100%',
          background: `linear-gradient(135deg, ${barColor}, ${barColor}cc)`,
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          padding: '0 0.5rem',
          gap: '0.35rem',
          overflow: 'hidden',
          border: hasExcursion ? `2px solid ${colors.danger}` : 'none',
          boxShadow: hasExcursion ? `0 0 8px ${colors.danger}40` : '0 2px 4px rgba(0,0,0,0.2)',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.02)';
          e.currentTarget.style.boxShadow = `0 4px 12px ${barColor}40`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = hasExcursion ? `0 0 8px ${colors.danger}40` : '0 2px 4px rgba(0,0,0,0.2)';
        }}
      >
        {hasExcursion && (
          <AlertTriangle size={12} color="white" style={{ flexShrink: 0 }} />
        )}
        <span
          style={{
            fontSize: '0.7rem',
            fontWeight: 600,
            color: 'white',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {shipment.productName?.split(' ')[0] || shipment.id}
        </span>
      </div>
    </div>
  );
}

// Timeline Row Component
function TimelineRow({ shipment, startDate, endDate, totalDays, onSelect, colors, isEven }) {
  const StatusIcon = STATUS_CONFIG[shipment.status]?.icon || Package;
  const statusColor = colors[STATUS_CONFIG[shipment.status]?.color || 'textMuted'];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '280px 1fr',
        borderBottom: `1px solid ${colors.border}`,
        background: isEven ? 'transparent' : colors.tableRowAlt,
      }}
    >
      {/* Shipment Info */}
      <div
        style={{
          padding: '0.75rem 1rem',
          borderRight: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          cursor: 'pointer',
          transition: 'background 0.15s ease',
        }}
        onClick={() => onSelect(shipment)}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = colors.bgElevated;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
      >
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            background: `${statusColor}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <StatusIcon size={18} color={statusColor} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: '0.8rem',
              fontWeight: 600,
              color: colors.textPrimary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {shipment.productName?.split(' ')[0] || 'Unknown'}
          </div>
          <div
            style={{
              fontSize: '0.7rem',
              color: colors.textMuted,
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            {shipment.id}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div
            style={{
              fontSize: '0.7rem',
              fontWeight: 500,
              color: statusColor,
            }}
          >
            {shipment.status}
          </div>
          <div
            style={{
              fontSize: '0.65rem',
              color: colors.textMuted,
            }}
          >
            {shipment.transitHours}h
          </div>
        </div>
      </div>

      {/* Timeline Bar */}
      <div
        style={{
          position: 'relative',
          padding: '0.5rem 0',
        }}
      >
        <TimelineBar
          shipment={shipment}
          startDate={startDate}
          endDate={endDate}
          totalDays={totalDays}
          onSelect={onSelect}
          colors={colors}
        />
      </div>
    </div>
  );
}

// Main Timeline Component
function ShipmentTimeline({ shipments, excursions, onSelectShipment }) {
  const { colors } = useTheme();
  const [daysToShow, setDaysToShow] = useState(14);
  const [offsetDays, setOffsetDays] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showExcursionsOnly, setShowExcursionsOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  // Calculate date range
  const { startDate, endDate, totalDays, dateMarkers } = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - daysToShow + 1 + offsetDays);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(end.getDate() + daysToShow);

    const markers = [];
    for (let i = 0; i <= daysToShow; i++) {
      const date = new Date(start);
      date.setDate(date.getDate() + i);
      markers.push(date);
    }

    return {
      startDate: start,
      endDate: end,
      totalDays: daysToShow,
      dateMarkers: markers,
    };
  }, [daysToShow, offsetDays]);

  // Filter shipments (without pagination limit)
  const filteredShipments = useMemo(() => {
    return shipments
      .filter(s => {
        if (statusFilter !== 'all' && s.status !== statusFilter) return false;
        if (showExcursionsOnly && !s.hasExcursion) return false;

        const shipDate = new Date(s.shipDate);
        const deliveryDate = new Date(shipDate.getTime() + s.transitHours * 60 * 60 * 1000);
        
        return shipDate <= endDate && deliveryDate >= startDate;
      })
      .sort((a, b) => new Date(b.shipDate) - new Date(a.shipDate));
  }, [shipments, statusFilter, showExcursionsOnly, startDate, endDate]);

  // Pagination
  const totalPages = Math.ceil(filteredShipments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedShipments = filteredShipments.slice(startIndex, endIndex);

  // Reset page when filters change
  useMemo(() => {
    setCurrentPage(1);
  }, [statusFilter, showExcursionsOnly, daysToShow, offsetDays]);

  // Stats
  const stats = useMemo(() => {
    const total = filteredShipments.length;
    const withExcursions = filteredShipments.filter(s => s.hasExcursion).length;
    const delivered = filteredShipments.filter(s => s.status === 'Delivered').length;
    const inTransit = filteredShipments.filter(s => s.status === 'In Transit').length;
    return { total, withExcursions, delivered, inTransit };
  }, [filteredShipments]);

  const handlePrevious = () => {
    setOffsetDays(prev => prev - Math.floor(daysToShow / 2));
  };

  const handleNext = () => {
    setOffsetDays(prev => Math.min(0, prev + Math.floor(daysToShow / 2)));
  };

  const handleToday = () => {
    setOffsetDays(0);
  };

  const handleZoomIn = () => {
    setDaysToShow(prev => Math.max(7, prev - 7));
  };

  const handleZoomOut = () => {
    setDaysToShow(prev => Math.min(30, prev + 7));
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const styles = {
    container: {
      background: colors.bgCard,
      border: `1px solid ${colors.border}`,
      borderRadius: '16px',
      overflow: 'hidden',
    },

    header: {
      padding: '1rem 1.25rem',
      borderBottom: `1px solid ${colors.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '1rem',
    },

    title: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.95rem',
      fontWeight: 600,
      color: colors.textPrimary,
    },

    controls: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      flexWrap: 'wrap',
    },

    controlGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      background: colors.bgElevated,
      borderRadius: '8px',
      padding: '0.25rem',
    },

    controlBtn: {
      padding: '0.4rem 0.6rem',
      background: 'transparent',
      border: 'none',
      borderRadius: '6px',
      color: colors.textSecondary,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.15s ease',
    },

    filterSelect: {
      padding: '0.4rem 0.75rem',
      background: colors.bgElevated,
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      color: colors.textPrimary,
      fontSize: '0.8rem',
      cursor: 'pointer',
    },

    toggleBtn: {
      padding: '0.4rem 0.75rem',
      background: 'transparent',
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      color: colors.textSecondary,
      fontSize: '0.75rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.35rem',
      transition: 'all 0.15s ease',
    },

    statsBar: {
      padding: '0.75rem 1.25rem',
      borderBottom: `1px solid ${colors.border}`,
      display: 'flex',
      gap: '1.5rem',
      background: colors.bgSecondary,
      fontSize: '0.75rem',
      flexWrap: 'wrap',
    },

    stat: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.35rem',
      color: colors.textMuted,
    },

    timelineHeader: {
      display: 'grid',
      gridTemplateColumns: '280px 1fr',
      borderBottom: `1px solid ${colors.border}`,
      background: colors.bgSecondary,
    },

    dateHeader: {
      display: 'flex',
      borderLeft: `1px solid ${colors.border}`,
    },

    dateCell: {
      flex: 1,
      padding: '0.5rem 0.25rem',
      textAlign: 'center',
      fontSize: '0.65rem',
      color: colors.textMuted,
      borderRight: `1px solid ${colors.border}`,
    },

    todayMarker: {
      background: `${colors.primary}15`,
      color: colors.primary,
      fontWeight: 600,
    },

    body: {
      minHeight: '300px',
      maxHeight: '500px',
      overflowY: 'auto',
    },

    emptyState: {
      padding: '3rem',
      textAlign: 'center',
      color: colors.textMuted,
    },

    footer: {
      padding: '0.75rem 1.25rem',
      borderTop: `1px solid ${colors.border}`,
      background: colors.bgSecondary,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '1rem',
    },

    legend: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'center',
      fontSize: '0.7rem',
      color: colors.textMuted,
    },

    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.35rem',
    },

    legendDot: {
      width: '10px',
      height: '10px',
      borderRadius: '3px',
    },

    pagination: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },

    pageButton: {
      padding: '0.35rem 0.5rem',
      background: colors.bgElevated,
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      color: colors.textSecondary,
      fontSize: '0.75rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.15s ease',
    },

    pageButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },

    pageInfo: {
      fontSize: '0.75rem',
      color: colors.textMuted,
      padding: '0 0.5rem',
    },

    itemsPerPageSelect: {
      padding: '0.35rem 0.5rem',
      background: colors.bgElevated,
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      color: colors.textPrimary,
      fontSize: '0.75rem',
      cursor: 'pointer',
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.title}>
          <Calendar size={18} color={colors.primary} />
          Shipment Timeline
        </div>

        <div style={styles.controls}>
          {/* Zoom Controls */}
          <div style={styles.controlGroup}>
            <button
              style={styles.controlBtn}
              onClick={handleZoomIn}
              title="Zoom In (fewer days)"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.bgHover || colors.bgCard;
                e.currentTarget.style.color = colors.textPrimary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = colors.textSecondary;
              }}
            >
              <ZoomIn size={16} />
            </button>
            <span style={{ fontSize: '0.75rem', color: colors.textMuted, padding: '0 0.5rem' }}>
              {daysToShow}d
            </span>
            <button
              style={styles.controlBtn}
              onClick={handleZoomOut}
              title="Zoom Out (more days)"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.bgHover || colors.bgCard;
                e.currentTarget.style.color = colors.textPrimary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = colors.textSecondary;
              }}
            >
              <ZoomOut size={16} />
            </button>
          </div>

          {/* Date Navigation */}
          <div style={styles.controlGroup}>
            <button
              style={styles.controlBtn}
              onClick={handlePrevious}
              title="Previous Period"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.bgHover || colors.bgCard;
                e.currentTarget.style.color = colors.textPrimary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = colors.textSecondary;
              }}
            >
              <ChevronLeft size={16} />
            </button>
            <button
              style={{
                ...styles.controlBtn,
                fontSize: '0.75rem',
                padding: '0.4rem 0.75rem',
              }}
              onClick={handleToday}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.primaryBg;
                e.currentTarget.style.color = colors.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = colors.textSecondary;
              }}
            >
              Today
            </button>
            <button
              style={{
                ...styles.controlBtn,
                opacity: offsetDays >= 0 ? 0.5 : 1,
                cursor: offsetDays >= 0 ? 'not-allowed' : 'pointer',
              }}
              onClick={handleNext}
              disabled={offsetDays >= 0}
              title="Next Period"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">All Status</option>
            <option value="In Transit">In Transit</option>
            <option value="Delivered">Delivered</option>
            <option value="Pending">Pending</option>
            <option value="Delayed">Delayed</option>
          </select>

          {/* Excursions Toggle */}
          <button
            style={{
              ...styles.toggleBtn,
              background: showExcursionsOnly ? colors.dangerBg : 'transparent',
              borderColor: showExcursionsOnly ? colors.danger : colors.border,
              color: showExcursionsOnly ? colors.danger : colors.textSecondary,
            }}
            onClick={() => setShowExcursionsOnly(!showExcursionsOnly)}
          >
            <AlertTriangle size={14} />
            Excursions Only
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div style={styles.statsBar}>
        <div style={styles.stat}>
          <Package size={14} color={colors.primary} />
          <span style={{ color: colors.textPrimary, fontWeight: 600 }}>{stats.total}</span>
          total shipments
        </div>
        <div style={styles.stat}>
          <Truck size={14} color={colors.info} />
          <span style={{ color: colors.info, fontWeight: 600 }}>{stats.inTransit}</span>
          in transit
        </div>
        <div style={styles.stat}>
          <CheckCircle size={14} color={colors.success} />
          <span style={{ color: colors.success, fontWeight: 600 }}>{stats.delivered}</span>
          delivered
        </div>
        <div style={styles.stat}>
          <AlertTriangle size={14} color={colors.danger} />
          <span style={{ color: colors.danger, fontWeight: 600 }}>{stats.withExcursions}</span>
          with excursions
        </div>
      </div>

      {/* Timeline Header with Dates */}
      <div style={styles.timelineHeader}>
        <div style={{ padding: '0.5rem 1rem', fontSize: '0.7rem', color: colors.textMuted, fontWeight: 600 }}>
          SHIPMENT
        </div>
        <div style={styles.dateHeader}>
          {dateMarkers.map((date, i) => (
            <div
              key={i}
              style={{
                ...styles.dateCell,
                ...(isToday(date) ? styles.todayMarker : {}),
              }}
            >
              <div>{formatDate(date)}</div>
              {isToday(date) && <div style={{ fontWeight: 700 }}>TODAY</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Timeline Body */}
      <div style={styles.body}>
        {paginatedShipments.length > 0 ? (
          paginatedShipments.map((shipment, index) => (
            <TimelineRow
              key={shipment.id}
              shipment={shipment}
              startDate={startDate}
              endDate={endDate}
              totalDays={totalDays}
              onSelect={onSelectShipment}
              colors={colors}
              isEven={index % 2 === 0}
            />
          ))
        ) : (
          <div style={styles.emptyState}>
            <Calendar size={40} color={colors.textMuted} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <div style={{ fontSize: '0.9rem', fontWeight: 500, marginBottom: '0.25rem' }}>
              No shipments in this time range
            </div>
            <div style={{ fontSize: '0.8rem' }}>
              Try adjusting the date range or filters
            </div>
          </div>
        )}
      </div>

      {/* Footer with Legend and Pagination */}
      <div style={styles.footer}>
        {/* Legend */}
        <div style={styles.legend}>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: colors.success }} />
            <span>Delivered</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: colors.info }} />
            <span>In Transit</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: colors.warning }} />
            <span>Pending</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: colors.danger, border: `2px solid ${colors.danger}` }} />
            <span>Excursion</span>
          </div>
        </div>

        {/* Pagination */}
        <div style={styles.pagination}>
          {/* Items per page */}
          <span style={{ fontSize: '0.7rem', color: colors.textMuted }}>Show:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={styles.itemsPerPageSelect}
          >
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          {/* Page navigation */}
          <button
            style={{
              ...styles.pageButton,
              ...(currentPage === 1 ? styles.pageButtonDisabled : {}),
            }}
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            title="First Page"
          >
            <ChevronsLeft size={14} />
          </button>
          <button
            style={{
              ...styles.pageButton,
              ...(currentPage === 1 ? styles.pageButtonDisabled : {}),
            }}
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            title="Previous Page"
          >
            <ChevronLeft size={14} />
          </button>

          <span style={styles.pageInfo}>
            {startIndex + 1}-{Math.min(endIndex, filteredShipments.length)} of {filteredShipments.length}
          </span>

          <button
            style={{
              ...styles.pageButton,
              ...(currentPage >= totalPages ? styles.pageButtonDisabled : {}),
            }}
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            title="Next Page"
          >
            <ChevronRight size={14} />
          </button>
          <button
            style={{
              ...styles.pageButton,
              ...(currentPage >= totalPages ? styles.pageButtonDisabled : {}),
            }}
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage >= totalPages}
            title="Last Page"
          >
            <ChevronsRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default ShipmentTimeline;